// Resolution timeline — 4-tier search with generated instructions
import { Database, Globe, Users, BookOpen, CheckCircle2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ResolutionTimelineNode } from './ResolutionTimelineNode'
import { TierActionButtons } from './TierActionButtons'
import { GeneratedInstructionsCard } from './GeneratedInstructionsCard'
import { KBResultCard } from './KBResultCard'
import { ExternalResultCard } from './ExternalResultCard'
import type { ResolutionTierResult, ResolutionTierName } from '@/types/issue'

interface ResolutionTimelineProps {
  tiers: ResolutionTierResult[]
  isRunning: boolean
  pausedAtTier: ResolutionTierName | null
  acceptedTier: ResolutionTierName | null
  onStartPipeline?: () => void
  onAcceptSolution: (tier: ResolutionTierName) => void
  onRejectAndContinue: () => void
  onSkipToExpert: () => void
  onSendToUser: (tier: ResolutionTierName) => void
  onExportPDF: (tier: ResolutionTierName) => void
  onExportDOCX: (tier: ResolutionTierName) => void
}

const TIER_CONFIG: Record<string, { label: string; agent: string; icon: JSX.Element }> = {
  kb: { label: 'Internal Knowledge Base', agent: 'KB Search Agent', icon: <Database className="h-4 w-4" /> },
  ms_docs: { label: 'Microsoft Learn Docs', agent: 'External Doc Search', icon: <BookOpen className="h-4 w-4" /> },
  community: { label: 'D365 Community Forums', agent: 'External Doc Search', icon: <Globe className="h-4 w-4" /> },
  expert: { label: 'Human Expert Assignment', agent: 'Expert Router Agent', icon: <Users className="h-4 w-4" /> },
}

export function ResolutionTimeline({
  tiers, isRunning, pausedAtTier, acceptedTier,
  onStartPipeline, onAcceptSolution, onRejectAndContinue, onSkipToExpert,
  onSendToUser, onExportPDF, onExportDOCX,
}: ResolutionTimelineProps): JSX.Element {
  const allPending = tiers.every((t) => t.status === 'pending')

  return (
    <div className="rounded-lg border bg-white p-4">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-kaycoo-charcoal">Resolution Pipeline</h3>
        {acceptedTier && (
          <Badge className="gap-1 bg-emerald-100 text-emerald-700 border-emerald-200">
            <CheckCircle2 className="h-3 w-3" /> Resolved
          </Badge>
        )}
        {allPending && onStartPipeline && (
          <Button size="sm" onClick={onStartPipeline} className="bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2">
            Search for Solutions
          </Button>
        )}
        {isRunning && (
          <span className="flex items-center gap-1.5 text-xs text-kaycoo-teal-dark">
            <span className="h-2 w-2 animate-pulse rounded-full bg-kaycoo-teal" />
            Searching...
          </span>
        )}
      </div>

      <div className="pl-1">
        {tiers.map((tier, i) => {
          const config = TIER_CONFIG[tier.tier]
          if (!config) return null
          const isPaused = pausedAtTier === tier.tier
          const isAccepted = acceptedTier === tier.tier

          return (
            <ResolutionTimelineNode
              key={tier.tier} tier={tier} label={config.label}
              agentLabel={config.agent} icon={config.icon}
              isLast={i === tiers.length - 1} isPaused={isPaused} isAccepted={isAccepted}
            >
              {renderTierResults(tier)}

              <GeneratedInstructionsCard
                instructions={tier.generatedInstructions ?? null}
                isGenerating={tier.isGeneratingInstructions ?? false}
              />

              {isPaused && (
                <TierActionButtons
                  tier={tier.tier}
                  confidence={tier.bestConfidence}
                  instructions={tier.generatedInstructions ?? null}
                  onAccept={() => onAcceptSolution(tier.tier)}
                  onReject={onRejectAndContinue}
                  onSkipToExpert={onSkipToExpert}
                  onSendToUser={() => onSendToUser(tier.tier)}
                  onExportPDF={() => onExportPDF(tier.tier)}
                  onExportDOCX={() => onExportDOCX(tier.tier)}
                />
              )}
            </ResolutionTimelineNode>
          )
        })}
      </div>
    </div>
  )
}

function renderTierResults(tier: ResolutionTierResult): JSX.Element | null {
  if (tier.tier === 'kb' && tier.results?.length) {
    return (
      <>
        {tier.results.map((r, j) => {
          const result = r as Record<string, unknown>
          return (
            <KBResultCard key={j}
              title={(result.title as string) ?? ''} description={(result.description as string) ?? ''}
              similarity={(result.similarity as number) ?? 0}
              confidence={((result.similarity as number) ?? 0) >= 0.85 ? 'high' : ((result.similarity as number) ?? 0) >= 0.70 ? 'medium' : 'low'}
              d365Version={result.d365Version as string} modules={result.affectedModules as string[]}
              sourceType={result.sourceType as string}
            />
          )
        })}
      </>
    )
  }
  if ((tier.tier === 'ms_docs' || tier.tier === 'community') && tier.results?.length) {
    return (
      <>
        {tier.results.map((r, j) => {
          const result = r as Record<string, unknown>
          return (
            <ExternalResultCard key={j}
              title={(result.title as string) ?? ''} snippet={(result.snippet as string) ?? ''}
              url={(result.url as string) ?? '#'} source={(result.source as string) ?? tier.tier}
              relevance={(result.relevance as number) ?? 0}
            />
          )
        })}
      </>
    )
  }
  if (tier.tier === 'expert' && tier.expertName) {
    return (
      <div className="mt-2 rounded-lg border border-purple-200 bg-purple-50 p-3 text-sm text-purple-800">
        Routed to <strong>{tier.expertName}</strong> for resolution
      </div>
    )
  }
  return null
}
